import React, {
  createContext,
  ReactNode,
  startTransition,
  useContext,
  useEffect,
} from "react";
import { Command } from "cmdk";
import { Dialog, IconButton, Tooltip } from "@mui/material";

// @ts-expect-error
import * as classes from "./CommandPalette.module.css";

import SearchBox from "decodable-ui/lib/components/SearchBox";
import {
  NavigateFunction,
  To,
  NavigateOptions,
  useNavigate,
} from "react-router";
import { usePath } from "../hooks/usePath";
import {
  AddCard,
  AddCircle,
  Person,
  PersonAdd,
  Upgrade,
  SwitchAccount,
  Receipt,
  Lock,
  Article,
  ChatBubble,
  Bolt,
} from "@mui/icons-material";
import Connection from "decodable-ui/lib/components/icons/Connection";
import Stream from "decodable-ui/lib/components/icons/Stream";
import Pipeline from "decodable-ui/lib/components/icons/Pipeline";
import { GroupIcon } from "../access/components/groups/GroupIcon";
import { RoleIcon } from "../access/components/roles/RoleIcon";
import { IfFeatureEnabled } from "./predicates";
import { useActiveAccount } from "../accounts/hooks/useAccounts";

const communitySlackUrl =
  "https://decodablecommunity.slack.com/join/shared_invite/zt-uvow71bk-Uf914umgpoyIbOQSxriJkA#/shared-invite/email";

type KeydownHandler = Parameters<
  typeof document.addEventListener<"keydown">
>[1];

export const CommandPaletteContext = createContext(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = React.useState(false);

  return (
    <CommandPaletteContext.Provider
      value={{
        setOpen,
      }}
    >
      <CommandPaletteDialog isOpen={isOpen} setOpen={setOpen} />
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const value = useContext(CommandPaletteContext);
  if (value == null) {
    return {
      setOpen: () => {
        throw new Error("Cannot open command palette. No provider present.");
      },
    };
  }
  return value;
}

export const CommandPaletteDialog = ({ isOpen, setOpen }) => {
  const { activeAccount } = useActiveAccount();

  // Toggle the menu when ⌘K is pressed
  useEffect(() => {
    const down: KeydownHandler = (e) => {
      if (activeAccount && e.key === "k" && (e.metaKey || e.altKey)) {
        e.preventDefault();
        setOpen((open: boolean) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [activeAccount]);

  const navigate = useNavigate();

  const handleNavigate = ((...args: Parameters<NavigateFunction>) => {
    setOpen(false);
    return startTransition(() => navigate(...args));
  }) as NavigateFunction;

  return (
    <Dialog
      open={isOpen}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
    >
      <CommandPalette navigate={handleNavigate as NavigateFunction} />
    </Dialog>
  );
};

export function CommandPalette({
  navigate,
  openTab = (url: string) => window.open(url, "_blank"),
}: {
  navigate: NavigateFunction;
  openTab?: (url: string) => void;
}) {
  const [filter, setFilter] = React.useState("");
  const { activeAccount, accountInfo } = useActiveAccount();

  const hasCustomPipelines =
    accountInfo?.subscription.capabilities.includes("custom_pipelines");

  const basePath = usePath({ accountName: activeAccount?.name });
  const connectionsPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "connection",
  });
  const streamsPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "stream",
  });
  const pipelinesPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "pipeline",
  });
  const usersPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "user",
  });
  const groupsPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "group",
  });
  const rolesPath = usePath({
    accountName: activeAccount?.name,
    resourceType: "role",
  });

  function handleNavigate(to: To, options?: NavigateOptions) {
    navigate(to, options);
    setFilter("");
  }

  // Note: We supply the 'value' prop, even when not strictly necessary,
  // as a workaround for https://github.com/pacocoursey/cmdk/issues/40.
  // Otherwise the filter is not correctly applied when backspacing.
  return (
    <Command className={classes["decodable-cmd-palette"]}>
      <SearchBox
        TextFieldProps={{
          inputRef: (e) => e?.focus(),
          placeholder: "Search commands",
          value: filter,
          onChange: (e) => setFilter(e.target.value),
          fullWidth: true,
          sx: { padding: 1 },
        }}
      />
      <Command.Input style={{ display: "none" }} value={filter} />
      <Command.List>
        <Command.Empty>No results</Command.Empty>
        <Command.Separator />
        <Command.Group heading="Manage Resources">
          <Command.Item
            onSelect={() => handleNavigate(connectionsPath)}
            value="View connections"
          >
            <Connection /> View connections
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate(`${connectionsPath}/create`)}
            value="Create a new connection"
          >
            <AddCircle /> Create a new connection
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate(streamsPath)}
            value="View streams"
          >
            <Stream /> View streams
          </Command.Item>
          <Command.Item
            value="Create a new stream"
            onSelect={() => handleNavigate(`${streamsPath}/create`)}
          >
            <AddCircle /> Create a new stream
          </Command.Item>
          <Command.Item
            value="View pipelines"
            onSelect={() => handleNavigate(pipelinesPath)}
          >
            <Pipeline /> View pipelines
          </Command.Item>
          <Command.Item
            value="Create a new pipeline"
            onSelect={() => handleNavigate(`${pipelinesPath}/create`)}
          >
            <AddCircle /> Create a new pipeline
          </Command.Item>
          {hasCustomPipelines ? (
            <Command.Item
              value="Upload a new custom pipeline"
              onSelect={() => {
                handleNavigate(`${pipelinesPath}/create?type=custom`);
              }}
            >
              <AddCircle /> Upload a new custom pipeline
            </Command.Item>
          ) : null}
          <Command.Item
            value="View secrets"
            onSelect={() => handleNavigate(`${basePath}/secrets`)}
          >
            <Lock /> View secrets
          </Command.Item>
        </Command.Group>

        <Command.Separator />
        <Command.Group heading="Manage Access Controls">
          <Command.Item
            value="Access Control Users"
            onSelect={() => handleNavigate(usersPath)}
          >
            <Person /> Users
          </Command.Item>
          <Command.Item
            value="Access Control Groups"
            onSelect={() => handleNavigate(groupsPath)}
          >
            <GroupIcon /> Groups
          </Command.Item>
          <Command.Item
            value="Access Control Roles"
            onSelect={() => handleNavigate(rolesPath)}
          >
            <RoleIcon /> Roles
          </Command.Item>
        </Command.Group>

        <Command.Separator />
        <Command.Group heading="Manage Account">
          <Command.Item
            value="Invite users"
            onSelect={() => handleNavigate(`${basePath}/account?tab=members`)}
          >
            <PersonAdd /> Invite users
          </Command.Item>
          <Command.Item
            value="Switch accounts"
            onSelect={() => handleNavigate(`/-/accounts`)}
          >
            <SwitchAccount /> Switch accounts
          </Command.Item>
        </Command.Group>

        <Command.Separator />
        <Command.Group heading="Get Help">
          <Command.Item
            value="Chat with the community"
            onSelect={() => openTab(communitySlackUrl)}
          >
            <ChatBubble /> Chat with the community
          </Command.Item>
          <Command.Item
            value="Read the documentation"
            onSelect={() => openTab("https://docs.decodable.co/")}
          >
            <Article /> Read the documentation
          </Command.Item>
        </Command.Group>
      </Command.List>
    </Command>
  );
}

export function OpenCommandPaletteButton() {
  const { setOpen } = useCommandPalette();

  return (
    <Tooltip arrow title={`Open command palette\n(⌘K or Alt+K)`}>
      <IconButton
        data-intercom-target="LaunchCommandPalette"
        onClick={() => setOpen(true)}
        sx={{ minWidth: 0, height: "2.5rem", aspectRatio: "1/1" }}
      >
        <Bolt />
      </IconButton>
    </Tooltip>
  );
}
